import { Accordion } from "@szhsin/react-accordion";
import { LogoIcon } from "assets/icons";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProfile } from "../../apis";
import { Route } from "../../common.types";
import { NavItem, ProfileBlock, NavAccordion } from "../../components";
import { Routes } from "../../constants/routes";
import { useSideBarCounters } from "../../hooks";

import styles from "./Sidebar.module.scss";

const Sidebar = () => {
  const { t } = useTranslation();
  const { user } = useProfile({ enabled: false });

  const { getCounterValueByRoutePath } = useSideBarCounters();

  const visibleRoutes = useMemo(() => {
    // A section header is declared on the first route of its section, which a
    // moderator may lack — carry it over to the first route they can see.
    let pendingSectionLabel: string | undefined;

    return Routes.reduce<{ route: Route; sectionLabel?: string }[]>(
      (acc, route) => {
        if (route.sectionLabel) {
          pendingSectionLabel = route.sectionLabel;
        }

        const isVisible =
          user.role !== "Moderator" ||
          !route.privilege ||
          user.privileges.some((privilege) => privilege === route.privilege);

        if (isVisible) {
          acc.push({ route, sectionLabel: pendingSectionLabel });
          pendingSectionLabel = undefined;
        }

        return acc;
      },
      []
    );
  }, [user]);

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebar__logo}>
        <LogoIcon width={173} height={41} />
      </div>

      <nav className={styles.sidebar__nav}>
        <Accordion transition transitionTimeout={250}>
          {visibleRoutes.map(({ route, sectionLabel }, index) => {
            const sectionHeader = sectionLabel ? (
              <div
                key={`section-${sectionLabel}`}
                className={styles.sidebar__sectionLabel}
              >
                {t(sectionLabel)}
              </div>
            ) : null;

            if (route.nestedRoutes && !route.hideNestedRoutes) {
              return (
                <Fragment key={route.label}>
                  {sectionHeader}
                  <NavAccordion route={route}>
                    {route.nestedRoutes.map((nestedRoute) => (
                      <NavItem
                        key={nestedRoute.label}
                        route={nestedRoute}
                        isNested
                        counter={getCounterValueByRoutePath(nestedRoute.path)}
                      />
                    ))}
                  </NavAccordion>
                </Fragment>
              );
            }

            return (
              <Fragment key={index}>
                {sectionHeader}
                <NavItem
                  route={route}
                  counter={getCounterValueByRoutePath(route.path)}
                />
              </Fragment>
            );
          })}
        </Accordion>
      </nav>

      <div className={styles.sidebar__profile}>
        <ProfileBlock />
      </div>
    </div>
  );
};

export default Sidebar;
